import { useEffect, useRef, useState } from 'react'
import { useRefreshGuard } from '../refreshGuards'
import { AudioLines, Download, X } from 'lucide-react'
import { useTasks } from '../TaskProvider'
import { audioUrl, uploadAudio } from '../audioTransfer'
import type { BibleAudio } from '../types'

export function BibleAudioSection({ audio, allAudio, path, onChange }: { audio: BibleAudio[]; allAudio: BibleAudio[]; path: string[]; onChange: (audio: BibleAudio[], field: string) => void }) {
  const { user } = useTasks()
  const [uploading, setUploading] = useState(false), [error, setError] = useState('')
  useRefreshGuard(uploading, 'Waiting for your audio upload to finish.')
  const controller = useRef<AbortController | null>(null), latest = useRef({ audio, allAudio, onChange })
  latest.current = { audio, allAudio, onChange }
  useEffect(() => () => controller.current?.abort(), [])
  return <section className="entry-audio">
    <h3>Audio & voice references</h3>
    <p>Dialogue takes, a character’s voice, or the sound of a place. Listen while you write.</p>
    {audio.map(recording => <figure key={recording.id} className="bible-recording">
      <figcaption><strong>{recording.name}</strong><span>{(recording.size / 1_000_000).toFixed(1)} MB</span>
        <a href={audioUrl(recording.assetId)} download={recording.name} className="icon-button" aria-label={'Download audio ' + recording.name}><Download size={15} /></a>
        <button type="button" className="icon-button" aria-label={'Remove audio ' + recording.name} onClick={() => onChange(audio.filter(item => item.id !== recording.id), 'remove-audio')}><X size={15} /></button>
      </figcaption>
      <audio controls preload="metadata" src={audioUrl(recording.assetId)} aria-label={'Play ' + recording.name}
        onError={() => setError('“' + recording.name + '” could not play here. Try downloading it, or use MP3 or WAV for wider browser support.')} />
      <label>Recording notes<input aria-label={'Notes for audio ' + recording.name} value={recording.caption} maxLength={1000}
        data-live-path={JSON.stringify([...path, 'audio', recording.id, 'caption'])} placeholder="What should we listen for?"
        onChange={e => onChange(audio.map(item => item.id === recording.id ? { ...item, caption: e.target.value } : item), 'audio-caption')} /></label>
    </figure>)}
    <label className={'button image-upload' + (uploading ? ' is-busy' : '')}><AudioLines size={15} />{uploading ? 'Uploading recording…' : 'Add audio'}
      <input type="file" aria-label="Upload Bible audio" accept="audio/*,.mp3,.wav,.m4a,.ogg,.webm,.flac" disabled={uploading || audio.length >= 12} onChange={async event => {
        const file = event.target.files?.[0]; event.target.value = ''
        if (!file) return
        const pending = new AbortController(); controller.current = pending
        setUploading(true); setError('')
        try {
          const saved = await uploadAudio(user.id, file, pending.signal)
          if (pending.signal.aborted) return
          if (latest.current.audio.length >= 12) throw new Error('Keep up to 12 recordings on each entry.')
          const sizes = new Map(latest.current.allAudio.map(item => [item.assetId, item.size])); sizes.set(saved.assetId, saved.size)
          if ([...sizes.values()].reduce((sum, size) => sum + size, 0) > 100_000_000) throw new Error('This story has reached its 100 MB audio allowance. Remove a recording from the Bible before adding this one.')
          latest.current.onChange([...latest.current.audio, { ...saved, id: crypto.randomUUID(), name: file.name.slice(0, 200), caption: '' }], 'add-audio')
        } catch (e) { if (!pending.signal.aborted) setError(e instanceof Error ? e.message : 'The recording could not be uploaded.') }
        finally { if (!pending.signal.aborted) setUploading(false) }
      }} />
    </label>
    <small>MP3, WAV, M4A, Ogg, WebM, FLAC · up to 25 MB each · 12 per entry · 100 MB per story</small>
    {error && <p role="alert" className="room-error">{error}</p>}
  </section>
}
